import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useTheme } from "@/context/ThemeContext";
import {
  Bot,
  Eye,
  BookOpen,
  BrainCircuit,
  Heart,
  Timer,
} from "lucide-react";

export default function EngineCapabilities() {
  const { t } = useTranslation("home");
  const { effectiveTheme } = useTheme();
  const isLight = effectiveTheme === "light";

  const capabilities = useMemo(
    () => [
      {
        icon: Bot,
        title: t("engine.item1.title"),
        description: t("engine.item1.desc"),
      },
      {
        icon: Eye,
        title: t("engine.item2.title"),
        description: t("engine.item2.desc"),
      },
      {
        icon: BookOpen,
        title: t("engine.item3.title"),
        description: t("engine.item3.desc"),
      },
      {
        icon: BrainCircuit,
        title: t("engine.item4.title"),
        description: t("engine.item4.desc"),
      },
      {
        icon: Heart,
        title: t("engine.item5.title"),
        description: t("engine.item5.desc"),
      },
      {
        icon: Timer,
        title: t("engine.item6.title"),
        description: t("engine.item6.desc"),
      },
    ],
    [t]
  );

  return (
    <section
      id="engine"
      className={`relative py-24 sm:py-32 ${
        isLight ? "bg-[#f0f0f5]" : "bg-cosmic-950"
      }`}
    >
      {/* Divider */}
      <div
        className={`mx-auto w-2/3 max-w-3xl h-px mb-24 sm:mb-32 bg-gradient-to-r from-transparent to-transparent ${
          isLight ? "via-black/10" : "via-white/10"
        }`}
      />

      <div className="relative w-full max-w-7xl mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-20 px-4 sm:px-0"
        >
          <h2
            className={`text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight leading-relaxed ${
              isLight ? "text-[#1c1c1e]" : "text-white"
            }`}
          >
            {t("engine.title")}
          </h2>
          <p
            className={`mt-5 text-base md:text-lg lg:text-xl font-light leading-7 ${
              isLight ? "text-[#6b6b7b]" : "text-silver-400"
            }`}
          >
            {t("engine.subtitle")}
          </p>
        </motion.div>

        <div className="overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3">
            {capabilities.map((cap, index) => (
              <motion.div
                key={cap.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`p-8 md:p-10 ${
                  index < 3
                    ? `max-md:border-b ${
                        isLight ? "max-md:border-black/10" : "max-md:border-white/10"
                      }`
                    : ""
                } ${
                  index % 3 !== 0
                    ? isLight
                      ? "md:border-l border-black/10"
                      : "md:border-l border-white/10"
                    : ""
                }`}
              >
                <div
                  className={`mb-5 w-12 h-12 flex items-center justify-center rounded-xl border ${
                    isLight
                      ? "bg-[#2d2d2d]/10 border-[#2d2d2d]/20"
                      : "bg-gold-500/10 border-gold-500/20"
                  }`}
                >
                  <cap.icon
                    className={`w-5 h-5 ${
                      isLight ? "text-[#2d2d2d]" : "text-gold-500"
                    }`}
                  />
                </div>
                <h3
                  className={`text-lg font-semibold mb-2 ${
                    isLight ? "text-[#1c1c1e]" : "text-white"
                  }`}
                >
                  {cap.title}
                </h3>
                <p
                  className={`text-[14.5px] font-light leading-7 md:text-[15px] ${
                    isLight ? "text-[#6e6e73]" : "text-silver-500"
                  }`}
                >
                  {cap.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}